import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  IconButton,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Line,
} from 'recharts';
import {
  TrendingDown,
  Refresh,
  WaterDrop,
  DeleteOutline,
  BoltOutlined,
} from '@mui/icons-material';
import { emissionsService, waterService, wasteService, renewableService } from '../../services';
import { useApp } from '../../contexts/AuthContext';

const SCOPE_COLORS = ['#ef4444', '#3b82f6', '#f59e0b'];
const WASTE_COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];
const RENEWABLE_COLORS = ['#f59e0b', '#3b82f6', '#06b6d4', '#10b981', '#8b5cf6', '#ec4899'];

interface WaterSummary {
  total_withdrawal: number;
  recycling_rate: number;
}

interface WasteSummary {
  total_waste: number;
  recycling_rate: number;
  by_disposal: Record<string, number>;
}

interface RenewableSummary {
  renewable_share_percentage: number;
  total_consumption_mwh: number;
  by_source: Record<string, number>;
}

export const Dashboard: React.FC = () => {
  const { selectedPeriod, facilities } = useApp();
  const [loading, setLoading] = useState(true);
  const [facilityFilter, setFacilityFilter] = useState<string>('all');

  // Emissions state
  const [emissionsByScope, setEmissionsByScope] = useState<{ labels: string[]; data: number[] }>({
    labels: ['Scope 1', 'Scope 2', 'Scope 3'],
    data: [0, 0, 0],
  });
  const [currentTotals, setCurrentTotals] = useState({ scope1: 0, scope2: 0, scope3: 0, total: 0 });
  const [monthlyData, setMonthlyData] = useState({
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    scope1: Array(12).fill(0),
    scope2: Array(12).fill(0),
    scope3: Array(12).fill(0),
    total: Array(12).fill(0),
  });

  // Water / Waste / Renewable state
  const [waterSummary, setWaterSummary] = useState<WaterSummary | null>(null);
  const [wasteSummary, setWasteSummary] = useState<WasteSummary | null>(null);
  const [renewableSummary, setRenewableSummary] = useState<RenewableSummary | null>(null);

  const displayYear = selectedPeriod?.year || new Date().getFullYear();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const period = selectedPeriod?.year?.toString();
      const year = selectedPeriod?.year || new Date().getFullYear();
      const facId = facilityFilter !== 'all' ? facilityFilter : undefined;

      const [summary, monthlyResult, waterResult, wasteResult, renewableResult] = await Promise.all([
        emissionsService.getSummary(period, facId).catch(() => null),
        emissionsService.getMonthlyEmissions(year, facId).catch(() => null),
        waterService.getSummary(period, facId).catch(() => null),
        wasteService.getSummary(period, facId).catch(() => null),
        renewableService.getSummary(period, facId).catch(() => null),
      ]);

      if (summary) {
        const scope1 = summary.scope1_total || 0;
        const scope2 = summary.scope2_location_based || 0;
        const scope3 = summary.scope3_total || 0;
        const total = scope1 + scope2 + scope3;

        setEmissionsByScope({ labels: ['Scope 1', 'Scope 2', 'Scope 3'], data: [scope1, scope2, scope3] });
        setCurrentTotals({ scope1, scope2, scope3, total });
      }

      if (monthlyResult?.monthly_emissions) {
        setMonthlyData({
          labels: monthlyResult.monthly_emissions.labels,
          scope1: monthlyResult.monthly_emissions.scope1,
          scope2: monthlyResult.monthly_emissions.scope2,
          scope3: monthlyResult.monthly_emissions.scope3,
          total: monthlyResult.monthly_emissions.total,
        });
      }

      setWaterSummary(waterResult);
      setWasteSummary(wasteResult);
      setRenewableSummary(renewableResult);
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedPeriod, facilityFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Chart data
  const monthlyBreakdown = monthlyData.labels.map((label, i) => ({
    month: label,
    'Scope 1': monthlyData.scope1[i],
    'Scope 2': monthlyData.scope2[i],
    'Scope 3': monthlyData.scope3[i],
    Total: monthlyData.total[i],
  }));

  const isMonthlyDataEmpty = useMemo(() => monthlyData.total.every(v => v === 0), [monthlyData]);
  const isPieDataEmpty = useMemo(() => emissionsByScope.data.every(v => v === 0), [emissionsByScope]);

  const wasteByDisposal = useMemo(() => {
    if (!wasteSummary?.by_disposal) return [];
    return Object.entries(wasteSummary.by_disposal).map(([name, value]) => ({ name, value }));
  }, [wasteSummary]);

  const renewableBySource = useMemo(() => {
    if (!renewableSummary?.by_source) return [];
    return Object.entries(renewableSummary.by_source).map(([name, value]) => ({ name, value }));
  }, [renewableSummary]);

  const fmt = (n: number, decimals = 1) => n.toLocaleString(undefined, { maximumFractionDigits: decimals });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
          ESG Dashboard
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <Select
              value={facilityFilter}
              onChange={(e) => setFacilityFilter(e.target.value)}
              sx={{ fontSize: '0.8rem', height: 32 }}
            >
              <MenuItem value="all">All Facilities</MenuItem>
              {facilities.map((f) => (
                <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <IconButton onClick={fetchData} size="small" sx={{ color: '#6b7280' }}>
            <Refresh sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>

      {/* Total Emissions + Scope Breakdown */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={12} md={7}>
          <Paper
            sx={{
              p: 2,
              height: '100%',
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              color: '#fff',
              borderRadius: 2,
            }}
          >
            <Typography variant="overline" sx={{ opacity: 0.9, letterSpacing: 1, fontSize: '0.65rem' }}>
              FY {displayYear} Total Emissions
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, my: 1 }}>
              {fmt(currentTotals.total, 2)} tCO2e
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 0.5,
                backgroundColor: 'rgba(255,255,255,0.2)', px: 1, py: 0.25, borderRadius: 1.5,
              }}>
                <TrendingDown sx={{ fontSize: 14 }} />
                <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                  Scope 1 + 2 + 3
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.7rem' }}>
                combined emissions
              </Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 2, height: '100%', border: '1px solid #e5e7eb', borderRadius: 2 }}>
            <Typography variant="overline" sx={{ color: '#6b7280', letterSpacing: 1, display: 'block', mb: 1.5, fontSize: '0.65rem' }}>
              Scope Breakdown
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {[
                { label: 'Scope 1', value: currentTotals.scope1, color: '#ef4444' },
                { label: 'Scope 2', value: currentTotals.scope2, color: '#3b82f6' },
                { label: 'Scope 3', value: currentTotals.scope3, color: '#f59e0b' },
              ].map(s => (
                <Box key={s.label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: s.color }} />
                    <Typography variant="caption" sx={{ color: '#374151', fontSize: '0.75rem' }}>{s.label}</Typography>
                  </Box>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#111827', fontSize: '0.75rem' }}>
                    {fmt(s.value, 2)} tCO2e
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Water / Waste / Renewable Summary Cards */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {/* Water Card */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid #e5e7eb', height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Box sx={{ width: 32, height: 32, borderRadius: 1.5, backgroundColor: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <WaterDrop sx={{ fontSize: 18, color: '#3b82f6' }} />
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>Water</Typography>
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#111827', mb: 0.5 }}>
              {fmt(waterSummary?.total_withdrawal ?? 0)} m³
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>Total Withdrawal</Typography>
            <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #f3f4f6' }}>
              <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.7rem' }}>
                Recycling Rate: <strong>{fmt(waterSummary?.recycling_rate ?? 0)}%</strong>
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Waste Card */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid #e5e7eb', height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Box sx={{ width: 32, height: 32, borderRadius: 1.5, backgroundColor: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <DeleteOutline sx={{ fontSize: 18, color: '#16a34a' }} />
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>Waste</Typography>
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#111827', mb: 0.5 }}>
              {fmt(wasteSummary?.total_waste ?? 0)} t
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>Total Waste</Typography>
            <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #f3f4f6' }}>
              <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.7rem' }}>
                Recycling Rate: <strong>{fmt(wasteSummary?.recycling_rate ?? 0)}%</strong>
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Renewable Energy Card */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid #e5e7eb', height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Box sx={{ width: 32, height: 32, borderRadius: 1.5, backgroundColor: '#fffbeb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <BoltOutlined sx={{ fontSize: 18, color: '#d97706' }} />
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>Renewable Energy</Typography>
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#111827', mb: 0.5 }}>
              {fmt(renewableSummary?.renewable_share_percentage ?? 0)}%
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>Renewable Share</Typography>
            <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #f3f4f6' }}>
              <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.7rem' }}>
                Total Consumption: <strong>{fmt(renewableSummary?.total_consumption_mwh ?? 0)} MWh</strong>
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Monthly Breakdown by Scope - Stacked Bar */}
      <Card sx={{ mb: 2, border: '1px solid #e5e7eb', boxShadow: 'none' }}>
        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, fontSize: '0.9rem' }}>
            Monthly Breakdown by Scope ({displayYear})
          </Typography>
          {isMonthlyDataEmpty ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
              <Typography variant="body2" color="text.secondary">
                No monthly emissions data recorded for {displayYear}
              </Typography>
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={monthlyBreakdown}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" style={{ fontSize: 11 }} />
                <YAxis label={{ value: 'tCO2e', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }} style={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ fontSize: 11 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="Scope 1" stackId="a" fill="#ef4444" />
                <Bar dataKey="Scope 2" stackId="a" fill="#3b82f6" />
                <Bar dataKey="Scope 3" stackId="a" fill="#f59e0b" />
                <Line type="monotone" dataKey="Total" stroke="#1a1a2e" strokeWidth={2} />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Three Pie Charts Row */}
      <Grid container spacing={1.5}>
        {/* Emissions by Scope */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: 340, border: '1px solid #e5e7eb', boxShadow: 'none' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, fontSize: '0.85rem' }}>
                Emissions by Scope
              </Typography>
              {isPieDataEmpty ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 270 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                    No emissions data
                  </Typography>
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height={270}>
                  <PieChart>
                    <Pie
                      data={emissionsByScope.data.map((value, i) => ({
                        name: emissionsByScope.labels[i],
                        value,
                      }))}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${((percent || 0) * 100).toFixed(1)}%`}
                      outerRadius={80}
                      dataKey="value"
                      style={{ fontSize: 11 }}
                    >
                      {emissionsByScope.data.map((_, i) => (
                        <Cell key={`scope-${i}`} fill={SCOPE_COLORS[i % SCOPE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ fontSize: 11 }} formatter={(value: unknown) => `${fmt(Number(value) || 0, 2)} tCO2e`} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Waste by Disposal */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: 340, border: '1px solid #e5e7eb', boxShadow: 'none' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, fontSize: '0.85rem' }}>
                Waste by Disposal Method
              </Typography>
              {wasteByDisposal.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 270 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                    No waste data
                  </Typography>
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height={270}>
                  <PieChart>
                    <Pie
                      data={wasteByDisposal}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${((percent || 0) * 100).toFixed(0)}%`}
                      outerRadius={80}
                      dataKey="value"
                      style={{ fontSize: 10 }}
                    >
                      {wasteByDisposal.map((_, i) => (
                        <Cell key={`waste-${i}`} fill={WASTE_COLORS[i % WASTE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ fontSize: 11 }} formatter={(value: unknown) => `${fmt(Number(value) || 0, 2)} t`} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Renewable by Source */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: 340, border: '1px solid #e5e7eb', boxShadow: 'none' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, fontSize: '0.85rem' }}>
                Renewable by Source
              </Typography>
              {renewableBySource.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 270 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                    No renewable data
                  </Typography>
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height={270}>
                  <PieChart>
                    <Pie
                      data={renewableBySource}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${((percent || 0) * 100).toFixed(0)}%`}
                      outerRadius={80}
                      dataKey="value"
                      style={{ fontSize: 10 }}
                    >
                      {renewableBySource.map((_, i) => (
                        <Cell key={`renew-${i}`} fill={RENEWABLE_COLORS[i % RENEWABLE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ fontSize: 11 }} formatter={(value: unknown) => `${fmt(Number(value) || 0, 2)} MWh`} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
